import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
    FaSpinner, FaPlus, FaSearch, FaHome, FaSyncAlt,
    FaEye, FaTrash, FaEdit, FaGavel, FaHandshake
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

import ProcessoModal from './ProcessoModal'; // Próximo passo: o modal de CRUD
// Importe a lista de clientes e devedores para os filtros e para o modal
// Importe os estilos 'ui'
import { ui } from '../utils/styles';

const ProcessoControlScreen = () => {
    const navigate = useNavigate();

    const [allProcessos, setAllProcessos] = useState([]);
    const [clientesContratantes, setClientesContratantes] = useState([]);
    const [devedores, setDevedores] = useState([]);

    // Filtros
    const [filterClienteId, setFilterClienteId] = useState('');
    const [filterNumero, setFilterNumero] = useState('');
    const [filterStatus, setFilterStatus] = useState(''); // Ex: 'Aberto', 'Fechado', 'Em Negociação'

    const [loading, setLoading] = useState(false);

    // Estados do Modal
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [selectedProcesso, setSelectedProcesso] = useState(null);

    const URL_BACKEND = process.env.REACT_APP_URL_BACKEND;
    const authHeader = useMemo(
        () => ({ Authorization: `Bearer ${localStorage.getItem('userToken')}` }),
        []
    );

    // --- Efeitos de Inicialização ---

    // 1. Carregar Dados Essenciais (Clientes e Devedores para Filtros e Modal)
    useEffect(() => {
        // fetchClientesAndDevedores();
    }, []);

    const fetchClientesAndDevedores = async () => {
        try {
            // Busca Clientes Contratantes
            const clientesRes = await axios.get(`${URL_BACKEND}/cliente`, { headers: authHeader });
            setClientesContratantes(clientesRes.data || []);

            // Busca Devedores
            const devedoresRes = await axios.get(`${URL_BACKEND}/devedor`, { headers: authHeader });
            setDevedores(devedoresRes.data || []);

            // Se for emitente (apenas um cliente), pré-seleciona o filtro
            const storedEmitenteId = localStorage.getItem('userIdEmitente');
            if (storedEmitenteId) {
                setFilterClienteId(String(storedEmitenteId));
            }

        } catch (error) {
            toast.error('Erro ao carregar dados de Cliente/Devedor.');
        }
    };

    // 2. Carregar Lista de Processos (Com base nos Filtros)
    useEffect(() => {
        fetchProcessos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterClienteId]); // Dispara ao mudar o cliente principal

    const fetchProcessos = async () => {
        setLoading(true);

        toast.success(`Rotina em construção.`);
        return;

        // Constrói a query de filtro
        const params = new URLSearchParams();
        if (filterClienteId) params.append('idCliente', filterClienteId);
        if (filterNumero) params.append('numeroProcesso', filterNumero);
        if (filterStatus) params.append('status', filterStatus);

        try {
            const { data } = await axios.get(
                `${URL_BACKEND}/processo`,
                { headers: authHeader, params }
            );
            const arr = Array.isArray(data) ? data : [];
            setAllProcessos(arr);
            toast.success(`Processos carregados: ${arr.length} registros.`);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Erro ao carregar Processos/Acordos.');
            setAllProcessos([]);
        } finally {
            setLoading(false);
        }
    };

    // --- Lógica do Modal e Ações ---

    const handleCreate = () => { setModalMode('create'); setSelectedProcesso(null); setShowModal(true); };
    const handleEdit = (processo) => { setModalMode('edit'); setSelectedProcesso(processo); setShowModal(true); };
    const handleView = (processo) => { setModalMode('view'); setSelectedProcesso(processo); setShowModal(true); };

    const handleDelete = async (id) => {
        if (!window.confirm(`Confirma a exclusão do Processo/Acordo ID ${id}? Isso pode afetar as dívidas vinculadas.`)) return;
        const toastId = toast.loading('Excluindo...');
        try {
            await axios.delete(`${URL_BACKEND}/processo/${id}`, { headers: authHeader });
            toast.success('Processo/Acordo excluído!', { id: toastId });
            fetchProcessos();
        } catch (error) {
            toast.error('Erro ao excluir processo. Verifique se não há parcelas geradas.', { id: toastId });
        }
    };

    const handleAcordo = (processo) => {
        // Navega para uma tela específica de gerenciamento de acordos/parcelas
        navigate(`/processo/${processo.id}/acordo`, { state: { processo } });
    };

    // --- Renderização ---

    const statusOptions = ['Aberto', 'Fechado', 'Em Negociação', 'Concluído', 'Cancelado'];

    return (
        <div style={ui.container}>

            {/* Cabeçalho */}
            <div style={ui.header}>
                <h1 style={ui.title}>
                    <FaGavel style={{ marginRight: 8 }} /> Gestão de Processos Judiciais / Acordos
                </h1>
                <button
                    onClick={() => navigate('/dashboard')}
                    style={ui.homeButton}
                >
                    <FaHome /> Voltar
                </button>
            </div>

            {/* Filtros */}
            <div style={{ ...ui.filters, justifyContent: 'flex-start' }}>

                <div style={ui.filterGroup}>
                    <label style={ui.label} htmlFor="filterCliente">Cliente Contratante</label>
                    <select
                        id="filterCliente"
                        style={ui.select}
                        value={filterClienteId}
                        onChange={e => setFilterClienteId(e.target.value)}
                        disabled={loading}
                    >
                        <option value="">Todos os Clientes</option>
                        {clientesContratantes.map(c => (
                            <option key={c.id} value={String(c.id)}>{c.nome}</option>
                        ))}
                    </select>
                </div>

                <div style={ui.filterGroup}>
                    <label style={ui.label} htmlFor="filterStatus">Status</label>
                    <select
                        id="filterStatus"
                        style={ui.select}
                        value={filterStatus}
                        onChange={e => setFilterStatus(e.target.value)}
                        disabled={loading}
                    >
                        <option value="">Todos os Status</option>
                        {statusOptions.map(s => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                </div>

                <div style={ui.filterGroup}>
                    <label style={ui.label} htmlFor="filterNumero">Nº Processo/Acordo</label>
                    <input
                        id="filterNumero"
                        style={ui.input}
                        value={filterNumero}
                        onChange={e => setFilterNumero(e.target.value)}
                        disabled={loading}
                        placeholder="Pesquisar número..."
                    />
                </div>

                {/* Botões de Ação */}
                <div style={{ ...ui.actionsRow, alignSelf: 'flex-end', minWidth: '200px' }}>
                    <button style={ui.buttonPrimary} onClick={fetchProcessos} disabled={loading}>
                        {loading ? <FaSpinner className="fa-spin" /> : <FaSearch />}
                    </button>
                    <button style={ui.buttonGreen} onClick={handleCreate}>
                        <FaPlus /> Novo Processo/Acordo
                    </button>
                </div>
            </div>

            {/* TABELA DE PROCESSOS */}
            <div style={ui.tableCard}>
                {loading ? (
                    <p style={{ padding: 16 }}>Carregando Processos...</p>
                ) : allProcessos.length === 0 ? (
                    <p style={{ color: 'red', padding: 16 }}>Nenhum processo judicial ou acordo encontrado.</p>
                ) : (
                    <div style={ui.tableScroll}>
                        <table style={ui.table}>
                            <thead style={ui.thead}>
                                <tr>
                                    <th style={{ ...ui.th, width: '80px' }}>ID</th>
                                    <th style={{ ...ui.th, width: '150px' }}>Nº Processo/Acordo</th>
                                    <th style={{ ...ui.th, width: '200px' }}>Cliente Contratante</th>
                                    <th style={{ ...ui.th, width: '200px' }}>Devedor Principal</th>
                                    <th style={{ ...ui.th, width: '100px' }}>Status</th>
                                    <th style={{ ...ui.th, width: '150px' }}>Data Início</th>
                                    <th style={ui.th}>Valor Total (Dívidas)</th>
                                    <th style={ui.thActions}>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {allProcessos.map((p, idx) => (
                                    <tr key={p.id} style={{ ...ui.tr, backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f9fafb' }}>
                                        <td style={ui.tdMono}>#{p.id}</td>
                                        <td style={ui.tdClamp}>{p.numero_processo || 'Acordo Direto'}</td>
                                        <td style={ui.tdClamp}>{p.nome_cliente_contratante || 'N/A'}</td>
                                        <td style={ui.tdClamp}>{p.nome_devedor || 'N/A'}</td>
                                        <td style={ui.tdClamp}>
                                            <span style={{ fontWeight: 'bold', color: p.status === 'Fechado' ? '#059669' : p.status === 'Em Negociação' ? '#d97706' : '#3b82f6' }}>
                                                {p.status}
                                            </span>
                                        </td>
                                        <td style={ui.tdClamp}>
                                            {p.data_inicio ? new Date(p.data_inicio).toLocaleDateString('pt-BR') : 'N/A'}
                                        </td>
                                        <td style={ui.tdMono}>R$ {Number(p.valor_total_dividas || 0).toFixed(2).replace('.', ',')}</td>

                                        <td style={ui.tdActions}>
                                            <button title="Visualizar" style={ui.actionView} onClick={() => handleView(p)}><FaEye /></button>
                                            <button title="Editar Processo" style={ui.actionEdit} onClick={() => handleEdit(p)}><FaEdit /></button>
                                            {/* Ação específica para ir para a gestão do Acordo/Parcelamento */}
                                            <button title="Gerenciar Acordo/Parcelamento" style={{ ...ui.actionView, background: '#a855f7' }} onClick={() => handleAcordo(p)}><FaHandshake /></button>
                                            <button title="Excluir" style={ui.actionDelete} onClick={() => handleDelete(p.id)}><FaTrash /></button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal de CRUD de Processo */}
            <ProcessoModal
                show={showModal}
                onClose={() => setShowModal(false)}
                processo={selectedProcesso}
                onSave={fetchProcessos}
                mode={modalMode}
                clientesContratantes={clientesContratantes}
                devedores={devedores}
            />

        </div>
    );
};

export default ProcessoControlScreen;